'use client';
import type { ReactElement } from 'react';
import { Fragment } from 'react';
import { Controller } from 'react-hook-form';

import { FormField } from '../shared/FormField';
import { ValuePairFields } from '../shared/ValuePairFields';
import { PaidByField } from '../shared/PaidByField';
import {
  InstitutionSearchField,
  type InstitutionValue
} from '../shared/InstitutionSearchField';
import type { InventoryFormTypeProps } from '../types';
import { getFormItem, getFormItemInfo, formatSelectLabel } from '../utils';
import { useFieldChanges } from '@/components/documents/ai-import/review-card/field-changes-context';

/**
 * Exported so `inventory-attribute-specs.tsx`'s `tax-liability` attribute
 * row ([INVDRAW-7] / VALE-78) binds to this list rather than hand-copying
 * it — a second copy would silently drift the moment a tax type is added
 * here without the drawer's select ever finding out.
 */
export const TAX_TYPE_OPTIONS = [
  { value: 'income-tax', label: 'Income Tax' },
  { value: 'capital-gains-tax', label: 'Capital Gains Tax' },
  { value: 'tax-return', label: 'Tax Return' },
  { value: 'land-tax', label: 'Land Tax' },
  { value: 'council-rates', label: 'Council Rates' },
  { value: 'water-rates', label: 'Water Rates' },
  { value: 'other', label: 'Other' }
] as const;

/**
 * Tax Liability form component
 * Handles both read-only and edit modes
 *
 * Fields:
 * - Tax Authority (InstitutionSearchField)
 * - Tax Type (select)
 * - Tax Period
 * - Lodgement Date
 * - Date of Death Balance / Current Balance
 * - Notes
 */
export function TaxLiabilityForm({
  form,
  mode,
  institutions = [],
  loadingInstitutions = false,
  onInstitutionSearch,
  isInstitutionEditable = true,
  hideEstateReferenceField,
  displayedInTitle = [],
  clickToCopy = false,
  contacts,
  loadingContacts,
  matterId,
  isCreateMode
}: InventoryFormTypeProps): ReactElement {
  const item = getFormItem(form);
  const itemInfo = getFormItemInfo(form);

  const isHidden = (fieldName: string) => displayedInTitle.includes(fieldName);

  const fieldChanges = useFieldChanges();
  const enrichment = (key: string) =>
    fieldChanges?.[key]?.changeType === 'enrichment'
      ? ('enrichment' as const)
      : undefined;

  if (mode === 'read') {
    return (
      <Fragment>
        {!isHidden('institution') && Boolean(item.institution) && (
          <InstitutionSearchField
            value={item.institution}
            onChange={() => {}}
            institutions={institutions}
            loadingInstitutions={loadingInstitutions}
            mode="read"
            institutionTypeName="Tax Authority"
          />
        )}

        {!isInstitutionEditable && !hideEstateReferenceField && (
          <FormField
            label="Estate Reference Number"
            value={(item.estateReference as string) || '\u2014'}
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}

        {Boolean(itemInfo.taxType) && (
          <FormField
            label="Tax Type"
            value={formatSelectLabel(
              itemInfo.taxType as string,
              TAX_TYPE_OPTIONS
            )}
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}

        {Boolean(itemInfo.taxPeriod) && (
          <FormField
            label="Tax Period"
            value={itemInfo.taxPeriod as string}
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}

        {Boolean(itemInfo.lodgementDate) && (
          <FormField
            label="Lodgement Date"
            value={itemInfo.lodgementDate as string}
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}

        <ValuePairFields
          mode="read"
          leftLabel="Date of Death Balance"
          leftValue={itemInfo.amountOwed as number}
          rightLabel="Current Balance"
          rightValue={itemInfo.currentBalance as number}
          clickToCopy={clickToCopy}
          leftChangeType={enrichment('itemInfo.amountOwed')}
          rightChangeType={enrichment('itemInfo.currentBalance')}
          negativeIsCredit
        />

        <PaidByField
          form={form}
          mode="read"
          contacts={contacts}
          clickToCopy={clickToCopy}
          isCreateMode={isCreateMode}
          matterId={matterId}
        />

        {Boolean(itemInfo.notes) && (
          <FormField
            label="Notes"
            value={itemInfo.notes as string}
            mode="read"
            multiline
            clickToCopy={clickToCopy}
          />
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Controller
        control={form.control}
        name="institution"
        render={({ field, fieldState }) => (
          <InstitutionSearchField
            value={field.value as InstitutionValue}
            onChange={field.onChange}
            onInstitutionSearch={onInstitutionSearch}
            institutions={institutions}
            loadingInstitutions={loadingInstitutions}
            mode={mode}
            institutionTypeName="Tax Authority"
            disabled={!isInstitutionEditable}
            error={fieldState.error?.message}
          />
        )}
      />

      {!isInstitutionEditable && !hideEstateReferenceField && (
        <Controller
          control={form.control}
          name="estateReference"
          render={({ field, fieldState }) => (
            <FormField
              label="Estate Reference Number"
              value={(field.value as string) || ''}
              mode="edit"
              placeholder="Enter estate reference number"
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      )}

      <div className="grid grid-cols-2 gap-3">
        <Controller
          control={form.control}
          name="itemInfo.taxType"
          render={({ field, fieldState }) => (
            <FormField
              label="Tax Type"
              value={(field.value as string) || ''}
              mode="edit"
              type="select"
              options={TAX_TYPE_OPTIONS}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={form.control}
          name="itemInfo.taxPeriod"
          render={({ field, fieldState }) => (
            <FormField
              label="Tax Period"
              value={(field.value as string) || ''}
              mode="edit"
              placeholder="e.g. FY2025-26"
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>

      <Controller
        control={form.control}
        name="itemInfo.lodgementDate"
        render={({ field, fieldState }) => (
          <FormField
            label="Lodgement Date"
            value={(field.value as string) || ''}
            mode="edit"
            type="date"
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={form.control}
        name="itemInfo.amountOwed"
        render={({ field: leftField, fieldState: leftState }) => (
          <Controller
            control={form.control}
            name="itemInfo.currentBalance"
            render={({ field: rightField, fieldState: rightState }) => (
              <ValuePairFields
                mode="edit"
                leftLabel="Date of Death Balance"
                leftValue={leftField.value as number}
                rightLabel="Current Balance"
                rightValue={rightField.value as number}
                onLeftChange={leftField.onChange}
                onRightChange={rightField.onChange}
                leftPlaceholder="0.00"
                rightPlaceholder="0.00"
                leftError={leftState.error?.message}
                rightError={rightState.error?.message}
                negativeIsCredit
              />
            )}
          />
        )}
      />

      <PaidByField
        form={form}
        mode="edit"
        contacts={contacts}
        loadingContacts={loadingContacts}
        isCreateMode={isCreateMode}
        matterId={matterId}
      />

      <Controller
        control={form.control}
        name="itemInfo.notes"
        render={({ field, fieldState }) => (
          <FormField
            label="Notes"
            value={(field.value as string) || ''}
            mode="edit"
            type="textarea"
            placeholder="Add notes..."
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />
    </Fragment>
  );
}
